<template>
    <div class="heading-holder">
        <h2>{{ translate('title') }}</h2>
        <p>{{ translate('description') }}</p>
    </div>

    <div style="opacity: 0; position: absolute">
        <div id="type-all" />
        <div v-for="(value, index) in filterOptions('type')" :id="`type-${value.value}`" :key="index" />
    </div>

    <div ref="filterAdvanced" class="filter-advanced">
        <ul class="tabs-container">
            <li :class="['tab', !filteredColumns.find((column) => column.type === 'type') ? 'selected' : null]">
                <button type="button" @click.prevent="selectSingleFilterOption('all', 'type')">
                    <span>{{ translate('filters.plan_types.all') }}</span>
                </button>
            </li>
            <li
                v-for="(value, index) in filterOptions('type')"
                :key="index"
                :class="[
                    'tab',
                    filteredColumns.find((column) => column.type === 'type')?.values.includes(value.value) ? 'selected' : null
                ]"
            >
                <button
                    type="button"
                    :class="`type-${value.value}`"
                    @click.prevent="selectSingleFilterOption(value.value, 'type')"
                >
                    <i v-if="value.value === 'server_outlet'" class="icon icon-percentage-solid"></i>
                    <span>{{ transformValueToReadableText(value.value, 'type') }}</span>
                </button>
            </li>
        </ul>

        <div :class="['filters-container', filtersShown ? 'open' : null]">
            <p>{{ translate('filters.advanced') }}</p>

            <div ref="filterMenu" class="types">
                <template v-for="(type, index) in filterableColumns" :key="index">
                    <div v-if="type !== 'type'" class="type">
                        <button type="button" class="button-selection" @click.prevent="toggleFilter(type)">
                            <span
                                v-if="
                                    Object.keys(filteredColumns).length && filteredColumns.find((column) => column.type === type)
                                "
                            >
                                {{ translate(`filters.columns.${type}`) }} ({{
                                    Object.keys(filteredColumns.find((column) => column.type === type).values).length
                                }})
                            </span>
                            <span v-else>{{ translate(`filters.columns.${type}`) }}</span>
                            <i class="icon icon-arrow-down"></i>
                        </button>

                        <ul v-if="filterMenuOpened === type" class="options">
                            <li v-for="option in filterOptions(type)" :key="option.id" class="option">
                                <button
                                    type="button"
                                    :class="[
                                        'button-option',
                                        filteredColumns.find((column) => column.type === type)?.values.includes(option.value)
                                            ? 'selected'
                                            : null
                                    ]"
                                    @click.prevent="selectFilterOption(option.value, type)"
                                >
                                    <span class="checkbox" />
                                    <span class="value" v-html="transformValueToReadableText(option.value, type)" />
                                </button>
                            </li>
                        </ul>
                    </div>
                </template>
            </div>
        </div>

        <button type="button" class="button-toggle-filters" @click.prevent="toggleFiltersContainer()">
            <i class="icon icon-sliders" />
            <span>{{ filtersShown ? translate('filters.hide_filters') : translate('filters.show_filters') }}</span>
        </button>
    </div>

    <div class="filter-billing-cycle">
        <p>{{ translate('filters.simple') }}</p>

        <div ref="selectionMenu" :class="['billing-cycle', selectionMenuOpened ? 'open' : null]">
            <button type="button" class="button-selection" @click.prevent="toggleSelection()">
                <span>{{ translate(`filters.billing_cycles.${selectionValue}`) }}</span>
                <i class="icon icon-arrow-down"></i>
            </button>

            <ul v-if="selectionMenuOpened" class="options">
                <li v-for="cycle in billingCycles" :key="cycle" class="option">
                    <button
                        type="button"
                        :class="['button-option', cycle === selectionValue ? 'selected' : null]"
                        @click.prevent="selectBillingCycle(cycle)"
                    >
                        {{ translate(`filters.billing_cycles.${cycle}`) }}
                    </button>
                </li>
            </ul>
        </div>
    </div>

    <div class="table">
        <table>
            <thead>
                <tr>
                    <th v-for="column in columns" :key="column">
                        <template v-if="column === 'prices'">
                            {{
                                translate(
                                    `table.columns.${selectionValue === 'monthly' ? 'prices_per_month' : 'prices_per_year'}`
                                )
                            }}
                        </template>

                        <template v-else>
                            {{ translate(`table.columns.${column}`) }}
                        </template>
                    </th>
                    <th>
                        <img
                            src="/images/ddos-protected-blue-stroke.svg"
                            class="protection"
                            alt=""
                            width="100"
                            height="100"
                            loading="lazy"
                        />
                    </th>
                </tr>
            </thead>
            <tbody>
                <template v-if="filteredPlans.length">
                    <tr
                        v-for="plan in filteredPlans"
                        :key="plan.id"
                        :class="
                            plan.metadata.stock.out_of_stock ? 'out_of_stock' : isHighlighted(plan) ? 'highlighted' : 'in_stock'
                        "
                    >
                        <td v-for="column in columns" :key="column">
                            <template v-if="column === 'prices'">
                                <div class="column-value-container price-container">
                                    <template
                                        v-if="plan.prices.find((cycle) => cycle.billing_cycle === selectionValue).old_price"
                                    >
                                        <ul>
                                            <li>
                                                <template v-if="currency === 'USD'">
                                                    <span class="price-base">
                                                        {{
                                                            formatPrice(
                                                                plan.prices.find(
                                                                    (cycle) => cycle.billing_cycle === selectionValue
                                                                ).old_price,
                                                                currency,
                                                                locale
                                                            )
                                                        }}
                                                    </span>
                                                    <span class="price-discounted">
                                                        {{
                                                            formatPrice(
                                                                plan.prices.find(
                                                                    (cycle) => cycle.billing_cycle === selectionValue
                                                                ).price,
                                                                currency,
                                                                locale
                                                            )
                                                        }}
                                                    </span>
                                                </template>

                                                <template v-else>
                                                    <span class="price-base">
                                                        {{
                                                            formatPrice(
                                                                plan.prices.find(
                                                                    (cycle) => cycle.billing_cycle === selectionValue
                                                                ).old_price,
                                                                currency,
                                                                locale
                                                            )
                                                        }}
                                                    </span>
                                                    <span class="price-discounted">
                                                        {{
                                                            formatPrice(
                                                                currency === 'BGN'
                                                                    ? plan.prices.find(
                                                                          (cycle) => cycle.billing_cycle === selectionValue
                                                                      ).price
                                                                    : plan.prices.find(
                                                                          (cycle) => cycle.billing_cycle === selectionValue
                                                                      ).price / currencyRatio,
                                                                currency,
                                                                locale
                                                            )
                                                        }}
                                                        /
                                                        {{
                                                            formatPrice(
                                                                currency === 'BGN'
                                                                    ? plan.prices.find(
                                                                          (cycle) => cycle.billing_cycle === selectionValue
                                                                      ).price / currencyRatio
                                                                    : plan.prices.find(
                                                                          (cycle) => cycle.billing_cycle === selectionValue
                                                                      ).price,
                                                                currency === 'BGN' ? 'EUR' : 'BGN',
                                                                locale
                                                            )
                                                        }}
                                                    </span>
                                                </template>
                                            </li>
                                        </ul>
                                    </template>

                                    <template v-else>
                                        <template v-if="currency === 'USD'">
                                            <span class="price-original">
                                                {{
                                                    formatPrice(
                                                        plan.prices.find((cycle) => cycle.billing_cycle === selectionValue).price,
                                                        currency,
                                                        locale
                                                    )
                                                }}
                                            </span>
                                        </template>

                                        <template v-else>
                                            <span class="price-original">
                                                {{
                                                    formatPrice(
                                                        currency === 'BGN'
                                                            ? plan.prices.find((cycle) => cycle.billing_cycle === selectionValue)
                                                                  .price
                                                            : plan.prices.find((cycle) => cycle.billing_cycle === selectionValue)
                                                                  .price / currencyRatio,
                                                        currency,
                                                        locale
                                                    )
                                                }}
                                                /
                                                {{
                                                    formatPrice(
                                                        currency === 'BGN'
                                                            ? plan.prices.find((cycle) => cycle.billing_cycle === selectionValue)
                                                                  .price / currencyRatio
                                                            : plan.prices.find((cycle) => cycle.billing_cycle === selectionValue)
                                                                  .price,
                                                        currency === 'BGN' ? 'EUR' : 'BGN',
                                                        locale
                                                    )
                                                }}
                                            </span>
                                        </template>
                                    </template>
                                </div>
                            </template>

                            <template v-else>
                                <template v-if="['cpu_model', 'ram_size', 'storage_size', 'traffic'].includes(column)">
                                    <span class="column-label">{{ translate(`filters.columns.${column}`) }}:</span>
                                    <div class="column-value-container">
                                        <template v-if="column === 'traffic' && plan.metadata.connectivity">
                                            <span class="column-value">
                                                {{ plan.metadata.connectivity }}
                                            </span>
                                        </template>

                                        <template v-else-if="column === 'storage_size'">
                                            <span class="column-value">
                                                {{ plan.metadata.storage_size }}
                                            </span>
                                        </template>

                                        <template v-else>
                                            <template v-if="column === 'cpu_model' && plan.metadata.cpu_specs">
                                                <span class="column-value">
                                                    {{ plan.metadata.cpu_specs }}
                                                </span>

                                                <span class="column-value-note">
                                                    <span>{{ transformValueToReadableText(plan.metadata[column], column) }}</span>
                                                </span>
                                            </template>

                                            <template v-else>
                                                <span class="column-value">
                                                    {{ transformValueToReadableText(plan.metadata[column], column) }}
                                                </span>
                                            </template>
                                        </template>

                                        <template v-if="column === 'ram_size' && plan.metadata.ram_type">
                                            <span class="column-value-note">
                                                <span>{{ plan.metadata.ram_type }}</span>
                                            </span>
                                        </template>

                                        <template v-if="column === 'storage_size' && plan.metadata.storage_type">
                                            <span class="column-value-note">
                                                <span>{{ plan.metadata.storage_type }}</span>
                                            </span>
                                        </template>

                                        <template v-if="column === 'traffic' && plan.metadata.connectivity">
                                            <span class="column-value-note">
                                                <span>{{ translate(`filters.traffic.${plan.metadata.traffic}`) }}</span>
                                            </span>
                                        </template>
                                    </div>
                                </template>

                                <template v-else-if="column === 'cpu_brand'">
                                    <span class="column-label">{{ translate(`filters.columns.${column}`) }}:</span>

                                    <picture class="column-cpu-brand">
                                        <template v-if="plan.metadata.cpu_brand === 'Intel'">
                                            <img
                                                src="/images/logo-intel.svg"
                                                alt="Intel logo"
                                                class="brand-intel"
                                                loading="async"
                                            />
                                        </template>

                                        <template v-else-if="plan.metadata.cpu_brand === 'AMD'">
                                            <img src="/images/logo-amd.svg" alt="AMD logo" class="brand-amd" loading="async" />
                                        </template>
                                    </picture>
                                </template>

                                <template v-else-if="column === 'plan'">
                                    <div class="column-value-container plan-container">
                                        <div
                                            v-if="
                                                plan.prices.find((cycle) => cycle.billing_cycle === selectionValue).discount >
                                                    10 ||
                                                (plan.metadata.label && plan.metadata.label !== null) ||
                                                (plan.metadata.label_new && plan.metadata.label_new !== null)
                                            "
                                            class="badges-container"
                                        >
                                            <!-- Promo Label -->
                                            <template
                                                v-if="
                                                    plan.prices.find((cycle) => cycle.billing_cycle === selectionValue).discount >
                                                    10
                                                "
                                            >
                                                <span :class="['badge', 'label', 'orange']">
                                                    {{ translate('table.badge.promo') }}
                                                </span>
                                            </template>

                                            <!-- Custom Label -->
                                            <template
                                                v-if="
                                                    plan.metadata.label &&
                                                    plan.metadata.label[locale] &&
                                                    (!['10% ПРОМО', '10% OFF'].includes(plan.metadata.label[locale]) ||
                                                        (['10% ПРОМО', '10% OFF'].includes(plan.metadata.label[locale]) &&
                                                            selectionValue === 'annually'))
                                                "
                                            >
                                                <span :class="['badge', 'label', 'red']">
                                                    {{
                                                        Object.keys(plan.metadata.label).length
                                                            ? plan.metadata.label[locale]
                                                            : plan.metadata.label
                                                    }}
                                                </span>
                                            </template>

                                            <!-- New Label -->
                                            <template v-if="plan.metadata.label_new">
                                                <span :class="['badge', 'label', 'green']">
                                                    {{
                                                        Object.keys(plan.metadata.label_new).length
                                                            ? plan.metadata.label_new[locale]
                                                            : plan.metadata.label_new
                                                    }}
                                                </span>
                                            </template>
                                        </div>

                                        <template v-if="plan.metadata.regions">
                                            <ul>
                                                <li v-for="region in plan.metadata.regions" :key="region.country">
                                                    <img
                                                        :src="`/images/flags/${region.country}.svg`"
                                                        :title="transformRegionToLocation(region)"
                                                        :class="region.status !== 'available' ? region.status : null"
                                                        width="640"
                                                        height="320"
                                                        loading="lazy"
                                                        decoding="async"
                                                    />
                                                </li>

                                                <template v-if="plan.metadata.type === 'server_outlet'">
                                                    <li
                                                        class="badge-holder"
                                                        :title="translate('filters.plan_types.server_outlet')"
                                                    >
                                                        <i class="icon icon-percentage-solid"></i>
                                                    </li>
                                                </template>
                                            </ul>
                                        </template>

                                        <h3>{{ plan.plan }}</h3>
                                    </div>
                                </template>

                                <template v-else>
                                    {{ plan.metadata[column] }}
                                </template>
                            </template>
                        </td>
                        <td>
                            <div class="flex-box">
                                <template v-if="plan.metadata.stock.out_of_stock">
                                    <span class="availability out_of_stock">
                                        {{ translate('table.status.availability', { count: 0 }) }}
                                    </span>
                                </template>

                                <template v-else>
                                    <a
                                        :href="`${clientArea}order/bare-metal/${plan.hostbill_code}?region=eu-sof-1&image=3&raid=1&locale=${locale}`"
                                        @click.prevent="
                                            makeGtmEvent(
                                                gtmEventName,
                                                { name: 'purchase', plan: plan.plan },
                                                `${clientArea}order/bare-metal/${plan.hostbill_code}?region=eu-sof-1&image=3&raid=1`
                                            )
                                        "
                                    >
                                        <span>{{ translate('table.button') }}</span>

                                        <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                                            <g fill-rule="evenodd">
                                                <path class="line-path" d="M0 5h7"></path>
                                                <path class="tip-path" d="M1 1l4 4-4 4"></path>
                                            </g>
                                        </svg>
                                    </a>

                                    <!--
                                        <template v-if="plan.metadata.stock.unlimited">
                                            <span class="availability delivery">{{ translate('table.status.delivery') }}</span>
                                        </template>

                                        <template v-else>
                                            <span class="availability in_stock">
                                                {{ translate('table.status.availability', { count: plan.metadata.stock.available }) }}
                                            </span>
                                        </template>
                                    -->

                                    <template v-if="plan.metadata.deploy_time">
                                        <span class="availability delivery">
                                            {{
                                                Object.keys(plan.metadata.deploy_time).length
                                                    ? plan.metadata.deploy_time[locale]
                                                    : plan.metadata.deploy_time
                                            }}
                                        </span>
                                    </template>
                                </template>
                            </div>
                        </td>
                    </tr>
                </template>

                <template v-else>
                    <tr class="no-results">
                        <td :colspan="columns.length">
                            {{ translate('table.no_results') }}
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>

        <div class="notes" v-html="translate('notes')" />
    </div>
</template>

<script setup>
    import { computed, ref, onMounted, onUnmounted, watch } from 'vue'

    import useFormatPrice from '../composables/useFormatPrice'
    import { useTranslate } from '../composables/useTranslate'

    const props = defineProps({
        /**
         * The locale to use for the translations.
         *
         * @type {String}
         * @required
         */
        locale: {
            type: String,
            required: true
        },
        /**
         * The currency to use for the prices.
         *
         * @type {String}
         * @required
         */
        currency: {
            type: String,
            required: true
        },
        /**
         * The plans to display in the table.
         *
         * @type {Object}
         * @required
         */
        plans: {
            type: Object,
            required: true
        },
        /**
         * The URL of the client area.
         *
         * @type {String}
         * @required
         */
        clientArea: {
            type: String,
            required: true
        },
        /**
         * The available billing cycles to choose from.
         *
         * @type {Array}
         * @required
         */
        billingCycles: {
            type: Array,
            required: true
        }
    })

    /**
     * The translation strings for the component.
     *
     * @type {Object}
     */
    const translations = {
        bg: {
            title: 'Цени на плановете',
            description:
                'Вижте списъка с предварително конфигурирани наети сървъри и изберете нает сървър според Вашите нужди. Предлагаме Bare Metal сървъри в наличност със срок на активиране до 10 мин., както и предефинирани сървъри, извън наличност със срок на доставка до 2-3 работни дни. Използвайте наличните филтри, за да видите цената.',
            notes: '<p>При поръчка на наети сървъри с Windows операционна система, се заплаща допълнителна такса за лиценз. Таксата зависи от модела на процесора.</p>',
            filters: {
                hide_filters: 'Сгъване на филтрите',
                show_filters: 'Разгъване на филтрите',
                simple: 'Изберете период на плащане:',
                advanced: 'Филтриране по:',
                columns: {
                    type: 'Тип',
                    regions: 'Регион',
                    plan: 'Конфигурация',
                    cpu_model: 'Процесор (модел)',
                    cpu_brand: 'Процесор (марка)',
                    ram_size: 'RAM памет',
                    storage_type: 'Тип на диска',
                    storage_size: 'Диск',
                    traffic: 'Свързаност',
                    prices: 'Цена'
                },
                plan_types: {
                    all: 'Всички планове',
                    server_outlet: 'Server Outlet',
                    server_46cores: '4-6 ядра',
                    server_812cores: '8-12 ядра',
                    server_1420cores: '14-20 ядра',
                    server_2432cores: '24-32 ядра',
                    server_over32cores: 'Над 32 ядра'
                },
                region: {
                    bg: '<span>България</span> ({data_centers})',
                    de: '<span>Германия</span> ({data_centers})',
                    gb: '<span>Великобритания</span> ({data_centers})'
                },
                traffic: {
                    unlimited: 'Неограничен трафик'
                },
                billing_cycles: {
                    free: 'Безплатен',
                    one_time: 'Еднократен',
                    weekly: 'Ежеседмично',
                    monthly: 'Месечен',
                    quarterly: 'На всеки 3 месеца',
                    semi_annually: 'На всеки 6 месеца',
                    annually: 'Годишен (спестявате 10%)',
                    biennially: 'На всеки 2 години',
                    triennially: 'На всеки 3 години'
                },
                billing_cycles_with_price: {
                    one_time: '{price} еднократно',
                    weekly: '{price} ежеседмично',
                    monthly: '{price} на месец',
                    quarterly: '{price} на всеки 3 месеца',
                    semi_annually: '{price} на всеки 6 месеца',
                    annually: '{price} на година',
                    biennially: '{price} на всеки 2 години',
                    triennially: '{price} на всеки 3 години'
                }
            },
            table: {
                button: 'Поръчайте',
                no_results: 'Няма намерени резултати за избраните опции.',
                columns: {
                    type: 'Тип',
                    regions: 'Регион',
                    plan: 'Конфигурация',
                    cpu_model: 'Процесор',
                    cpu_brand: 'Марка',
                    ram_size: 'RAM памет',
                    storage_type: 'Тип на диска',
                    storage_size: 'Диск',
                    traffic: 'Свързаност',
                    prices: 'Цена',
                    prices_per_month: 'Цена на месец',
                    prices_per_year: 'Цена на година'
                },
                region: {
                    bg: 'България',
                    de: 'Германия',
                    gb: 'Великобритания'
                },
                region_extended: {
                    'eu-sof': 'София, България ({data_center})',
                    'eu-fra': 'Франкфурт, Германия ({data_center})',
                    'eu-lon': 'Лондон, Великобритания ({data_center})'
                },
                badge: {
                    promo: 'промо'
                },
                status: {
                    availability: 'Няма наличност | 1 сървър в наличност | {count} сървъра в наличност',
                    delivery: 'Доставка до 2 работни дни',
                    soon: 'скоро'
                }
            }
        },
        en: {
            title: 'Prices',
            description:
                'See the list of pre-configured dedicated servers and choose a dedicated server according to your needs. We offer Bare Metal servers in stock with an activation period of up to 10 minutes, as well as predefined servers, out of stock with a delivery period of 2-3 business days. Use the available filters to see the price.',
            notes: '<p>When ordering dedicated servers with Windows operating system, an additional license fee is charged. The fee depends on the processor model.</p>',
            filters: {
                hide_filters: 'Hide Filters',
                show_filters: 'Show Filters',
                simple: 'Select a billing cycle:',
                advanced: 'Filter by:',
                columns: {
                    type: 'Type',
                    regions: 'Region',
                    plan: 'Configuration',
                    cpu_model: 'CPU Model',
                    cpu_brand: 'CPU Brand',
                    ram_size: 'RAM',
                    storage_type: 'Storage Type',
                    storage_size: 'Storage Size',
                    traffic: 'Connectivity',
                    prices: 'Price'
                },
                plan_types: {
                    all: 'All Plans',
                    server_outlet: 'Server Outlet',
                    server_46cores: '4-6 Cores',
                    server_812cores: '8-12 Cores',
                    server_1420cores: '14-20 Cores',
                    server_2432cores: '24-32 Cores',
                    server_over32cores: 'Over 32 Cores'
                },
                region: {
                    bg: 'Bulgaria ({data_centers})',
                    de: 'Germany ({data_centers})',
                    gb: 'United Kingdom ({data_centers})'
                },
                traffic: {
                    unlimited: 'Unlimited Traffic'
                },
                billing_cycles: {
                    free: 'Free',
                    one_time: 'One Time',
                    weekly: 'Weekly',
                    monthly: 'Monthly',
                    quarterly: 'Quarterly',
                    semi_annually: 'Semi-Annually',
                    annually: 'Annually (save 10%)',
                    biennially: 'Biennially',
                    triennially: 'Triennially'
                },
                billing_cycles_with_price: {
                    one_time: '{price} one time',
                    weekly: '{price} weekly',
                    monthly: '{price} monthly',
                    quarterly: '{price} quarterly',
                    semi_annually: '{price} semi-annually',
                    annually: '{price} annually',
                    biennially: '{price} biennially',
                    triennially: '{price} triennially'
                }
            },
            table: {
                button: 'Order Now',
                no_results: 'No results found for the selected options.',
                columns: {
                    type: 'Type',
                    regions: 'Region',
                    plan: 'Configuration',
                    cpu_model: 'CPU Model',
                    cpu_brand: 'CPU Brand',
                    ram_size: 'RAM',
                    storage_type: 'Storage Type',
                    storage_size: 'Storage Size',
                    traffic: 'Connectivity',
                    prices: 'Price',
                    prices_per_month: 'Price Per Month',
                    prices_per_year: 'Price Per Year'
                },
                region: {
                    bg: 'Bulgaria',
                    de: 'Germany',
                    gb: 'United Kingdom'
                },
                region_extended: {
                    'eu-sof': 'Sofia, Bulgaria ({data_center})',
                    'eu-fra': 'Frankfurt, Germany ({data_center})',
                    'eu-lon': 'London, United Kingdom ({data_center})'
                },
                badge: {
                    promo: 'promo'
                },
                status: {
                    availability: 'Out of stock | 1 server in stock | {count} servers in stock',
                    delivery: 'Delivery within 2 business days',
                    soon: 'soon'
                }
            }
        }
    }

    const { translate } = useTranslate(props.locale, translations)
    const { formatPrice } = useFormatPrice()

    const selectionMenu = ref(null) // The selection dropdown menu element.
    const selectionMenuOpened = ref(false) // Whether the selection dropdown menu is opened.
    const selectionValue = ref('monthly') // The selected billing cycle by default.
    const columns = ['cpu_brand', 'plan', 'cpu_model', 'ram_size', 'storage_size', 'traffic', 'prices'] // The columns to display in the table.
    const filterableColumns = ref(['type', 'regions', 'cpu_brand', 'ram_size', 'storage_type']) // The columns that can be filtered.
    const filterAdvanced = ref(null) // The advanced filters container element.
    const filterMenu = ref(null) // The filter dropdown menu element.
    const filterMenuOpened = ref(null) // Whether the filter dropdown menu is opened.
    const filteredColumns = ref([]) // The columns and their values that are filtered.
    const filtersShown = ref(false) // Whether the advanced filters are shown.
    const gtmEventName = 'dedicated_servers_link' // The GTM event name.
    const highlightCriteria = ref({}) // The criteria for highlighting plans.
    const currencyRatio = 1.95583 // Currency ratio EUR to BGN.

    // Define the column paths for highlighting.
    const columnPaths = {
        plan: 'plan',
        cpu_model: 'metadata.cpu_model'
        // cpu_brand: 'metadata.cpu_brand'
        // ram_size: 'metadata.ram_size',
        // storage_size: 'metadata.storage_size',
        // traffic: 'metadata.traffic'
    }

    /**
     * Scroll to a given element on the page.
     *
     * @param {Element} element - The element to scroll to.
     */
    const scrollToElement = (element) => {
        const headerElement = document.querySelector('.page-header')

        const elementOffsetTop = element.getBoundingClientRect().top + window.pageYOffset
        const headerHeight = headerElement ? headerElement.offsetHeight : 0

        window.scrollTo({
            top: Number(elementOffsetTop - headerHeight) - 20,
            behavior: 'smooth'
        })
    }

    /**
     * Toggle the visibility of the billing cycle selection menu.
     */
    const toggleSelection = () => {
        selectionMenuOpened.value = !selectionMenuOpened.value
    }

    /**
     * Toggle the visibility of the filter dropdown menu for a given column type.
     */
    const toggleFilter = (type) => {
        filterMenuOpened.value = filterMenuOpened.value === type ? null : type
    }

    /**
     * Toggle the visibility of the advanced filters container.
     */
    const toggleFiltersContainer = () => {
        filtersShown.value = !filtersShown.value

        setTimeout(() => {
            scrollToElement(filterAdvanced.value)
        }, 100)
    }

    /**
     * Extract unique values for a given column type.
     *
     * @param {String} type - The column type.
     * @return {Array} - Unique values for the column type.
     */
    const extractUniqueValues = (type) => {
        if (!props.plans.length) return []
        if (!filterableColumns.value.includes(type)) return []

        const uniqueValues = new Set()

        props.plans.forEach((plan) => {
            if (!plan.metadata[type]) return

            if (plan.metadata[type] instanceof Array) {
                plan.metadata[type].forEach((value) => uniqueValues.add(value.country))
                return
            }

            uniqueValues.add(plan.metadata[type])
        })

        return Array.from(uniqueValues).sort((a, b) => a - b)
    }

    /**
     * Extract unique region values for a given region code.
     *
     * @param {String} code - The region code.
     * @return {String} - Unique region values.
     */
    const extractUniqueRegionValues = (code) => {
        if (!props.plans.length) return []

        const uniqueValues = new Set()

        props.plans.forEach((plan) => {
            if (!plan.metadata.regions) return

            plan.metadata.regions.forEach((region) => {
                if (region.country === code) {
                    uniqueValues.add(region.name)
                }
            })
        })

        let values = Array.from(uniqueValues).sort((a, b) => a - b)

        return values.toString().replace(/,/g, ', ')
    }

    /**
     * Get the filter options for a given column type.
     *
     * @param {String} type - The column type.
     * @return {Array} - The filter options.
     */
    const filterOptions = (type) => {
        const values = extractUniqueValues(type)

        if (type === 'type') {
            const typeOrder = [
                'server_outlet',
                'server_46cores',
                'server_812cores',
                'server_1420cores',
                'server_2432cores',
                'server_over32cores'
            ]

            return values.sort((a, b) => typeOrder.indexOf(a) - typeOrder.indexOf(b)).map((value) => ({ value }))
        }

        return values.sort((a, b) => a - b).map((value) => ({ value }))
    }

    /**
     * Computed property to filter plans based on selected filter options.
     */
    const filteredPlans = computed(() => {
        if (filteredColumns.value.length === 0) {
            return props.plans
        }

        return props.plans.filter((plan) => {
            return filteredColumns.value.every((column) => {
                if (column.type === 'regions') {
                    return column.values.some((value) => {
                        return plan.metadata.regions.some((region) => region.country === value)
                    })
                }

                return column.values.includes(plan.metadata[column.type])
            })
        })
    })

    /**
     * Utility function to transform the value of a column to a more readable text.
     *
     * @param {String|Number} value - The value to transform.
     * @param {String} type - The type of the value.
     * @return {String} - The transformed value.
     */
    const transformValueToReadableText = (value, type) => {
        switch (type) {
            case 'type':
                return translate(`filters.plan_types.${value}`)
            case 'regions':
                return translate(`filters.region.${value}`, { data_centers: extractUniqueRegionValues(value) })
            case 'cpu_model':
                return `${value}`
            case 'storage_size':
                return `${value} GB`
            default:
                return value
        }
    }

    /**
     * Utility function to make the region value more readable.
     *
     * @param {Object} region - The region object.
     * @return {String} - Readable string representation of the region.
     */
    const transformRegionToLocation = (region) => {
        const regionStatus = (value) => {
            if (value !== 'available') {
                return ` (${translate(`table.status.${value}`)})`
            }

            return ''
        }

        return `${translate(`table.region_extended.${region.city}`, { data_center: region.name })}${regionStatus(region.status)}`
    }

    /**
     * Get the number of months in a billing cycle.
     *
     * @param {String} cycle - The billing cycle.
     * @return {Number} - The number of months in the billing cycle.
     */
    const transformBillingCycleToMonths = (cycle) => {
        switch (cycle) {
            case 'weekly':
                return 1 / 4
            case 'monthly':
                return 1
            case 'quarterly':
                return 3
            case 'semi_annually':
                return 6
            case 'annually':
                return 12
            case 'biennially':
                return 24
            case 'triennially':
                return 36
            default:
                return 1
        }
    }

    /**
     * Clear all filter options for a given type.
     *
     * @param {String} type - The type of the filter options to clear.
     */
    const clearFilterOptionsByType = (type) => {
        filteredColumns.value = filteredColumns.value.filter((column) => column.type !== type)
    }

    /**
     * Select a billing cycle and close the selection menu.
     *
     * @param {String} option - The selected billing cycle.
     */
    const selectBillingCycle = (option) => {
        if (option === selectionValue.value) return

        if (!props.billingCycles.includes(option)) {
            console.error(`The option "${option}" is not valid. The available options are: ${props.billingCycles.join(', ')}`)
            return
        }

        selectionValue.value = option
        selectionMenuOpened.value = false
    }

    /**
     * Select a filter option and update the filteredColumns array.
     *
     * @param {String|Number} optionId - The ID of the selected option.
     * @param {String} optionType - The type of the selected option.
     */
    const selectFilterOption = (optionId, optionType) => {
        // Find the existing entry for the current type in filteredColumns
        let column = filteredColumns.value.find((col) => col.type === optionType)

        if (column) {
            // Check if the option is already selected
            const optionIndex = column.values.indexOf(optionId)

            if (optionIndex > -1) {
                // If the option is already selected, remove it from the values array
                column.values.splice(optionIndex, 1)

                // If no values are left for this type, remove the entire type from filteredColumns
                if (column.values.length === 0) {
                    filteredColumns.value = filteredColumns.value.filter((col) => col.type !== optionType)
                }
            } else {
                // If the option is not selected, add it to the values array
                column.values.push(optionId)
            }
        } else {
            // If the type does not exist in filteredColumns, create a new entry
            filteredColumns.value.push({
                type: optionType,
                values: [optionId]
            })
        }
    }

    /**
     * Select a single filter option and clear all other options for the same type.
     *
     * @param {String|Number} optionId - The ID of the selected option.
     * @param {String} optionType - The type of the selected option.
     */
    const selectSingleFilterOption = (optionId = null, optionType, hasPushState = true) => {
        clearFilterOptionsByType(optionType)

        if (optionType === 'type') {
            if (optionId !== 'all') {
                filteredColumns.value.push({
                    type: optionType,
                    values: [optionId]
                })
            }

            if (hasPushState) {
                window.dataLayer = window.dataLayer || []
                window.dataLayer.push({
                    event: gtmEventName,
                    name: 'tab',
                    tab: optionId
                })

                window.history.pushState(null, null, `#type-${optionId}`)
            }
        }
    }

    /**
     * Make a Google Tag Manager event when clicking a button.
     *
     * @param {String} event - The GTM event name.
     * @param {String} data - The GTM event data.
     * @param {String} custom_link - Whether to open a custom link in a new tab.
     * @return {String} - The GTM event string.
     */
    const makeGtmEvent = (event, data, custom_link = null) => {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({ event, ...data })

        if (custom_link) {
            window.open(custom_link, '_blank')
        }

        return true
    }

    /**
     * Close the selection dropdown menu when clicking outside it.
     *
     * @param {Event} event
     */
    const handleClickOutside = (event) => {
        if (selectionMenu.value && !selectionMenu.value.contains(event.target)) {
            selectionMenuOpened.value = false
        }

        if (filterMenuOpened.value && !filterMenu.value.contains(event.target)) {
            filterMenuOpened.value = null
        }
    }

    window.addEventListener('select-tab', (event) => {
        if (event.detail.tab && filterOptions('type').find((option) => option.value === event.detail.tab)) {
            selectSingleFilterOption(event.detail.tab, 'type')
        }
    })

    /**
     * Check if a plan should be highlighted based on URL query parameters.
     *
     * @param {Object} plan - The plan to check.
     * @return {Boolean} - Whether the plan should be highlighted.
     */
    const isHighlighted = (plan) => {
        // If no highlight criteria, return false
        if (Object.keys(highlightCriteria.value).length === 0) return false

        // Check each highlight criteria
        return Object.entries(highlightCriteria.value).some(([column, values]) => {
            if (column in columnPaths) {
                // Get the path to the value
                const path = columnPaths[column].split('.')

                // Navigate to the value using the path
                let value = plan
                for (const key of path) {
                    if (value === undefined || value === null) return false
                    value = value[key]
                }

                // Check if the value is in the list of values to highlight
                return value !== undefined && value !== null && values.includes(value)
            }

            return false
        })
    }

    /**
     * Parse URL parameters and set highlight criteria.
     */
    const parseUrlParameters = () => {
        const url = new URL(window.location.href)
        const params = new URLSearchParams(url.search)

        // Clear existing highlight criteria
        highlightCriteria.value = {}

        // Check all URL parameters
        for (const [param, value] of params.entries()) {
            if (value) {
                // Split by comma if multiple values are provided
                const values = value.split(',')
                if (values.length > 0) highlightCriteria.value[param] = values
            }
        }

        // If we have highlight criteria, scroll to the table
        if (Object.keys(highlightCriteria.value).length > 0) {
            setTimeout(() => scrollToElement(filterAdvanced.value), 300)
        }
    }

    onMounted(() => {
        document.addEventListener('click', handleClickOutside)

        // Parse URL parameters for highlighting
        parseUrlParameters()

        // Handle hash if present
        if (window.location.hash) {
            const type = window.location.hash.replace('#type-', '')

            if (type && filterOptions('type').find((option) => option.value === type)) {
                selectSingleFilterOption(type, 'type')
            }
        } else if (!filteredColumns.value.length) {
            selectSingleFilterOption('all', 'type', false)
        }
    })

    /**
     * Remove event listener for clicking outside the selection dropdown menu.
     */
    onUnmounted(() => {
        document.removeEventListener('click', handleClickOutside)
    })
</script>
